import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, concatMap, from, map, Observable, Subject } from 'rxjs';
import { LoggerService } from '@lib.core/logger/logger.service';

interface NavigationRequest {
  commands: any[];
  extras?: NavigationExtras;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationInteractor {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly logger = LoggerService.getLoggerService();
  private readonly listener$ = new Subject<NavigationRequest>();

  constructor() {
    this.listener$.pipe(
      concatMap((request: NavigationRequest) => this.navigate(request)),
      takeUntilDestroyed()
    ).subscribe({
      next: (res: {success: boolean, errorMessage: string | undefined}) => {
        if (!res.success) {
          this.logger.warning(`${res.errorMessage}: a queued navigation failed or prevented by guard`);
        }
      },
      error: (e: Error) => this.logger.error(e)
    });
  }

  private navigate(request: NavigationRequest): Observable<{success: boolean, errorMessage: string | undefined}> {
    return from(this.router.navigate(request.commands, request.extras)).pipe(
      map((success: boolean) => ({success, errorMessage: request.errorMessage})),
      catchError((err: Error) => { throw new Error(`${request.errorMessage}: ${err.message}`); }),
    );
  }

  queueNavigation(commands: any[], extras?: NavigationExtras, errorMessage?: string) {
    this.listener$.next({commands, extras, errorMessage});
  }

  removeQueryParam(param: string) {
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    if (param in queryParams) {
      delete queryParams[param];
      this.queueNavigation([], {
        queryParams,
        replaceUrl: true,
      });
    }
  }
}
